import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { Check, FileText, X, AlertTriangle, Edit3, ChevronRight } from 'lucide-react';

const fmtKr = (n) =>
  new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));

const ymd = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Invoicing convention: bill last completed month after close. Default the
// range to the previous calendar month so users land on the right window
// without fiddling.
const lastFullMonthRange = () => {
  const now = new Date();
  const firstOfThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDayLastMonth = new Date(firstOfThis.getTime() - 86400000);
  const firstOfLast = new Date(lastDayLastMonth.getFullYear(), lastDayLastMonth.getMonth(), 1);
  return { from: ymd(firstOfLast), to: ymd(lastDayLastMonth) };
};

/**
 * InvoiceTimeImportModal — focused modal for picking billable time entries
 * to push onto a customer invoice.
 *
 * Mirrors the visual frame of PayslipTimeImportModal (modern-wage-modal +
 * ti-* idioms) so the two import flows feel like one product. Functionally
 * preserves the dual-step flow of the inline TimeImportPanel: pick a date
 * range + grouping → load preview → check rows + edit descriptions →
 * commit.
 *
 * Props:
 *   show, onClose
 *   previewData: { groups: [...], totals: { amountExclVat } }
 *   loading: preview load in flight
 *   error: string | null
 *   importing: confirm in flight
 *   onLoadPreview: ({ fromDate, toDate, groupBy }) => void
 *   onConfirmImport: ({ fromDate, toDate, groupBy, selectedGroups }) => void
 */
const InvoiceTimeImportModal = ({
  show,
  onClose,
  previewData,
  loading,
  error,
  importing,
  onLoadPreview,
  onConfirmImport,
}) => {
  const initialRange = useRef(lastFullMonthRange()).current;
  const [fromDate, setFromDate] = useState(initialRange.from);
  const [toDate, setToDate] = useState(initialRange.to);
  const [groupBy, setGroupBy] = useState('by_project');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const [descriptions, setDescriptions] = useState({});

  // Auto-select all groups on each fresh preview load
  useEffect(() => {
    if (previewData?.groups) {
      setSelectedKeys(new Set(previewData.groups.map((g) => g.groupKey)));
      setDescriptions({});
    }
  }, [previewData]);

  // Auto-load preview when the modal opens, and re-fetch (debounced) when
  // any control changes while the modal is visible. Keeps the latest
  // onLoadPreview in a ref so this effect's deps stay control-only.
  const onLoadPreviewRef = useRef(onLoadPreview);
  useEffect(() => {
    onLoadPreviewRef.current = onLoadPreview;
  }, [onLoadPreview]);

  useEffect(() => {
    if (!show) return undefined;
    const t = setTimeout(() => {
      onLoadPreviewRef.current({ fromDate, toDate, groupBy });
    }, 250);
    return () => clearTimeout(t);
  }, [show, fromDate, toDate, groupBy]);

  const groups = previewData?.groups || [];
  const allSelected = groups.length > 0 && groups.every((g) => selectedKeys.has(g.groupKey));
  const totalAmount = groups
    .filter((g) => selectedKeys.has(g.groupKey))
    .reduce((acc, g) => acc + (Number(g.totalAmount) || 0), 0);

  const toggleSelect = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) setSelectedKeys(new Set());
    else setSelectedKeys(new Set(groups.map((g) => g.groupKey)));
  };

  const toggleExpand = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedGroups = groups
      .filter((g) => selectedKeys.has(g.groupKey))
      .map((g) => ({
        groupKey: g.groupKey,
        description: descriptions[g.groupKey] || undefined,
      }));
    onConfirmImport({ fromDate, toDate, groupBy, selectedGroups });
  };

  const handleClose = () => {
    if (importing) return;
    setSelectedKeys(new Set());
    setDescriptions({});
    onClose();
  };

  return (
    <>
      <style>{`
        .modern-wage-modal .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }
        .modern-wage-modal .modal-header {
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          border-bottom: 1px solid #e5e7eb;
          border-radius: 16px 16px 0 0;
          padding: 2rem 2rem 1.5rem 2rem;
        }
        .modern-wage-modal .modal-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.5rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }
        .modern-wage-modal .modal-body {
          padding: 2rem;
          background: #f8fafc;
          max-height: 70vh;
          overflow-y: auto;
        }
        .modern-wage-modal .modal-footer {
          background: white;
          border-top: 1px solid #e5e7eb;
          border-radius: 0 0 16px 16px;
          padding: 1.5rem 2rem;
        }

        .ti-info-section {
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          border: 1px solid #86efac;
          border-radius: 12px;
          padding: 1.25rem 1.5rem;
          margin-bottom: 1.5rem;
          display: flex;
          gap: 1rem;
          align-items: flex-start;
        }
        .ti-info-section.warning {
          background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
          border-color: #fcd34d;
        }
        .ti-info-section.error {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          border-color: #fca5a5;
        }
        .ti-info-icon { color: var(--bs-primary); flex-shrink: 0; }
        .ti-info-section.warning .ti-info-icon { color: #b45309; }
        .ti-info-section.error .ti-info-icon { color: #b91c1c; }
        .ti-info-text {
          color: #166534;
          font-size: 0.875rem;
          line-height: 1.6;
          flex: 1;
        }
        .ti-info-section.warning .ti-info-text { color: #78350f; }
        .ti-info-section.error .ti-info-text { color: #7f1d1d; }
        .ti-info-text strong { font-weight: 600; }

        .ti-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 1rem 1.25rem;
          margin-bottom: 1rem;
        }

        .ti-controls {
          display: flex;
          gap: 0.75rem;
          align-items: flex-end;
          flex-wrap: wrap;
        }
        .ti-controls .field { display: flex; flex-direction: column; gap: 0.25rem; }
        .ti-controls label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #64748b;
        }
        .ti-controls input[type="date"],
        .ti-controls select {
          padding: 0 0.75rem;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 0.875rem;
          background: white;
          color: #1e293b;
          min-width: 160px;
          height: 40px;
          line-height: 1.2;
          font-family: inherit;
          box-sizing: border-box;
        }
        .ti-controls input[type="date"]:hover,
        .ti-controls select:hover {
          border-color: #cbd5e1;
        }
        .ti-controls input[type="date"]:focus,
        .ti-controls select:focus {
          outline: none;
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 3px rgba(68, 129, 78, 0.1);
        }
        .ti-controls select {
          appearance: none;
          -webkit-appearance: none;
          -moz-appearance: none;
          padding-right: 2.25rem;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none' stroke='%2364748b' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='1.5 1.5 6 6 10.5 1.5'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 0.85rem center;
          background-size: 11px 8px;
          cursor: pointer;
        }
        .ti-controls-spinner {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          color: #64748b;
          font-size: 0.8125rem;
          height: 40px;
          margin-left: auto;
        }
        .ti-controls-card.is-loading { transition: background-color 0.2s ease; }

        .ti-rows-table {
          width: 100%;
          border-collapse: collapse;
        }
        .ti-rows-table th {
          text-align: left;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          padding: 0.5rem 0.75rem;
          border-bottom: 1px solid #e5e7eb;
          white-space: nowrap;
        }
        .ti-rows-table th.num,
        .ti-rows-table th.center { text-align: right; }
        .ti-rows-table th.center { text-align: center; }
        .ti-rows-table td {
          padding: 0.75rem;
          border-bottom: 1px solid #f1f5f9;
          font-size: 0.875rem;
          color: #1e293b;
          vertical-align: middle;
        }
        .ti-rows-table tr:last-child td { border-bottom: none; }
        .ti-rows-table td.num {
          text-align: right;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .ti-rows-table td.center { text-align: center; }
        .ti-rows-table td.amount { font-weight: 600; }
        .ti-desc-wrap {
          position: relative;
          display: flex;
          align-items: center;
        }
        .ti-desc-input {
          width: 100%;
          border: 1px solid transparent;
          background: transparent;
          font-size: 0.875rem;
          color: #1e293b;
          padding: 0.35rem 1.75rem 0.35rem 0.5rem;
          border-radius: 6px;
          outline: none;
          transition: border-color 0.15s ease, background 0.15s ease;
        }
        .ti-desc-input:hover { border-color: #e2e8f0; background: #f8fafc; }
        .ti-desc-input:focus {
          border-color: var(--bs-primary);
          background: white;
        }
        .ti-desc-icon {
          position: absolute;
          right: 0.5rem;
          color: #cbd5e1;
          pointer-events: none;
          transition: color 0.15s ease;
        }
        .ti-rows-table tbody tr:hover .ti-desc-icon { color: #94a3b8; }
        .ti-desc-input:focus + .ti-desc-icon { color: var(--bs-primary); }

        .ti-expand-toggle {
          flex-shrink: 0;
          width: 22px;
          height: 22px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid transparent;
          background: transparent;
          color: #94a3b8;
          border-radius: 6px;
          cursor: pointer;
          margin-right: 0.25rem;
          transition: transform 0.15s ease, background 0.15s ease, color 0.15s ease;
        }
        .ti-expand-toggle:hover {
          background: #f1f5f9;
          color: #475569;
        }
        .ti-expand-toggle.is-open {
          transform: rotate(90deg);
          color: var(--bs-primary);
        }
        .ti-rows-table tr.is-expanded td {
          background: #f8fafc;
          border-bottom-color: transparent;
        }
        .ti-detail-row td {
          background: #f8fafc;
          padding-top: 0;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }
        .ti-entries-list {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          padding: 0.5rem 0.75rem;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
        }
        .ti-entry-row {
          display: grid;
          grid-template-columns: 88px 140px 1fr 56px;
          gap: 0.75rem;
          padding: 0.4rem 0.25rem;
          font-size: 0.8125rem;
          color: #475569;
          align-items: baseline;
          border-bottom: 1px solid #f1f5f9;
        }
        .ti-entry-row:last-child { border-bottom: none; }
        .ti-entry-date {
          font-variant-numeric: tabular-nums;
          color: #64748b;
        }
        .ti-entry-emp {
          font-weight: 500;
          color: #1e293b;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ti-entry-desc {
          color: #475569;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ti-entry-hours {
          text-align: right;
          font-variant-numeric: tabular-nums;
          color: #1e293b;
          font-weight: 500;
        }
        .ti-rows-table tfoot td {
          background: #f8fafc;
          font-weight: 600;
          border-top: 2px solid #e5e7eb;
          border-bottom: none;
        }

        .ti-empty {
          background: white;
          border: 1px dashed #d1d5db;
          border-radius: 12px;
          padding: 3rem 2rem;
          text-align: center;
          color: #64748b;
        }
        .ti-empty h4 {
          margin: 1rem 0 0.5rem 0;
          color: #374151;
          font-size: 1.125rem;
          font-weight: 600;
        }
        .ti-empty p {
          margin: 0;
          font-size: 0.875rem;
          line-height: 1.5;
        }
        .ti-empty-icon { color: #9ca3af; }

        .ti-loading {
          text-align: center;
          padding: 3rem 0;
          color: #64748b;
        }

        .modern-btn {
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 500;
          font-size: 0.875rem;
          border: none;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 120px;
          justify-content: center;
        }
        .btn-secondary-modern {
          background: #f1f5f9;
          color: #64748b;
          border: 1px solid #e2e8f0;
        }
        .btn-secondary-modern:hover { background: #e2e8f0; color: #475569; }
        .btn-primary-modern { background: var(--bs-primary); color: white; }
        .btn-primary-modern:hover:not(:disabled) {
          background: var(--primary-hover);
          transform: translateY(-1px);
        }
        .btn-primary-modern:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        @media (max-width: 768px) {
          .modern-wage-modal .modal-header,
          .modern-wage-modal .modal-body,
          .modern-wage-modal .modal-footer {
            padding: 1.5rem 1rem;
          }
          .modern-btn { width: 100%; }
          .ti-controls input[type="date"],
          .ti-controls select { min-width: 0; width: 100%; }
        }
      `}</style>

      <Modal show={show} onHide={handleClose} size="lg" className="modern-wage-modal">
        <Modal.Header closeButton>
          <Modal.Title>
            <FileText size={24} color="var(--bs-primary)" />
            Importera tidrapport
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className={`ti-card ti-controls-card${loading ? ' is-loading' : ''}`}>
            <div className="ti-controls">
              <div className="field">
                <label htmlFor="ti-from">Från</label>
                <input
                  id="ti-from"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="ti-to">Till</label>
                <input
                  id="ti-to"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
              <div className="field">
                <label htmlFor="ti-group">Gruppera</label>
                <select
                  id="ti-group"
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value)}
                >
                  <option value="by_project">Per projekt</option>
                  <option value="by_person">Per person</option>
                  <option value="by_entry">Per post</option>
                </select>
              </div>
              {loading && (
                <div className="ti-controls-spinner" aria-live="polite">
                  <Spinner animation="border" size="sm" />
                  <span>Laddar…</span>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="ti-info-section error">
              <AlertTriangle size={20} className="ti-info-icon" />
              <div className="ti-info-text">{error}</div>
            </div>
          )}

          {previewData && groups.length > 0 && (
            <div className="ti-card">
              <table className="ti-rows-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        aria-label="Markera alla grupper"
                      />
                    </th>
                    <th>Beskrivning</th>
                    <th className="num">Antal</th>
                    <th className="num">Pris/st</th>
                    <th className="center">Moms</th>
                    <th className="num">Belopp</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => {
                    const isExpanded = expandedKeys.has(g.groupKey);
                    const entries = g.entries || [];
                    const hasEntries = entries.length > 0;
                    return (
                      <React.Fragment key={g.groupKey}>
                        <tr className={isExpanded ? 'is-expanded' : ''}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedKeys.has(g.groupKey)}
                              onChange={() => toggleSelect(g.groupKey)}
                              aria-label={`Markera ${g.label}`}
                            />
                          </td>
                          <td>
                            <div className="ti-desc-wrap">
                              {hasEntries && (
                                <button
                                  type="button"
                                  className={`ti-expand-toggle${isExpanded ? ' is-open' : ''}`}
                                  onClick={() => toggleExpand(g.groupKey)}
                                  aria-expanded={isExpanded}
                                  aria-label={isExpanded ? 'Dölj poster' : 'Visa poster'}
                                  title={`${entries.length} ${entries.length === 1 ? 'post' : 'poster'}`}
                                >
                                  <ChevronRight size={14} />
                                </button>
                              )}
                              <input
                                type="text"
                                className="ti-desc-input"
                                value={descriptions[g.groupKey] ?? g.label}
                                onChange={(e) =>
                                  setDescriptions((prev) => ({ ...prev, [g.groupKey]: e.target.value }))
                                }
                                aria-label={`Beskrivning för ${g.label}`}
                              />
                              <Edit3 size={13} className="ti-desc-icon" aria-hidden="true" />
                            </div>
                          </td>
                          <td className="num">{g.totalHours}h</td>
                          <td className="num">{fmtKr(g.billingRate)} kr</td>
                          <td className="center">{g.taxRate}</td>
                          <td className="num amount">{fmtKr(g.totalAmount)} kr</td>
                        </tr>
                        {isExpanded && hasEntries && (
                          <tr className="ti-detail-row">
                            <td></td>
                            <td colSpan={5}>
                              <div className="ti-entries-list" role="list">
                                {entries.map((e) => (
                                  <div key={e.entryId} className="ti-entry-row" role="listitem">
                                    <span className="ti-entry-date">{e.date}</span>
                                    {e.employeeName && (
                                      <span className="ti-entry-emp">{e.employeeName}</span>
                                    )}
                                    <span className="ti-entry-desc" title={e.description || ''}>
                                      {e.description || '—'}
                                    </span>
                                    <span className="ti-entry-hours">{e.hours}h</span>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5}>Totalt valt (exkl. moms)</td>
                    <td className="num">{fmtKr(totalAmount)} kr</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {!loading && previewData && groups.length === 0 && (
            <div className="ti-empty">
              <FileText size={48} className="ti-empty-icon" />
              <h4>Inga fakturerbara tidposter</h4>
              <p>
                Det finns inga godkända, ofakturerade tidposter för denna kund i den valda
                perioden. Justera datumintervallet eller godkänn tidrapporter först.
              </p>
            </div>
          )}

        </Modal.Body>

        <Modal.Footer>
          <div className="d-flex justify-content-between w-100 align-items-center">
            <small className="text-muted">
              {previewData && groups.length > 0
                ? `${selectedKeys.size} av ${groups.length} valda`
                : 'Justera period eller gruppering ovan'}
            </small>
            <div className="d-flex gap-2">
              <button
                type="button"
                className="modern-btn btn-secondary-modern"
                onClick={handleClose}
                disabled={importing}
              >
                <X size={16} />
                Avbryt
              </button>
              <button
                type="button"
                className="modern-btn btn-primary-modern"
                onClick={handleConfirm}
                disabled={importing || loading || groups.length === 0 || selectedKeys.size === 0}
              >
                {importing ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <Check size={16} />
                    Lägg till på faktura
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default InvoiceTimeImportModal;
